'use client';
import { projectName } from '../../../packages/plugin-shared/projectChoice.mjs';
import { useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { useBrainExecutionTargets } from '../../lib/queries';
import { executionRefKey, type ProjectExecutionRef } from '../../lib/types';
import { ProjectIcon } from '../../components/ui/ProjectIcon';
import { ConnectionStatus } from '../../components/ui/ConnectionStatus';
import { ProjectMenuContent } from '../../components/ui/ProjectMenu';
import { DropdownMenu, DropdownMenuTrigger } from '../../components/ui/shadcn/dropdown-menu';
import { Button } from '../../components/ui/Button';
import { useOperationDock } from '../../lib/operationDock';
import { useBrainChat, useBrainChatStatus } from './BrainChatProvider';

/** Selection comes from durable execution identity, never from cwd or conversation filing. */
export function ProjectPicker({ variant = 'full' }: { variant?: 'full' | 'compact' }) {
  const { t } = useTranslation();
  const s = t.projects;
  const { activeSessionId, selectProjectExecution, projectMove } = useBrainChat();
  const { telemetry, readOnly, childFocus, guest } = useBrainChatStatus();
  const projects = useBrainExecutionTargets();
  const [open, setOpen] = useState(false);
  const moving = projectMove.status === 'saving';
  const moveStatus = projectMove.status;
  const retryTarget = projectMove.retryTarget;
  const dock = useOperationDock();
  const sessionRef = useRef(activeSessionId);
  sessionRef.current = activeSessionId;
  const reported = telemetry.projectRef;
  const reportedKey = reported ? executionRefKey(reported) : '';
  const target = reported;
  // A host project is a project: whoever the API offers it to may work in it, and the daemon confines
  // the turn to that project's root exactly as it confines a managed one to its environment.
  const items = projects.data ?? [];
  const current = target ? items.find((project) => executionRefKey(project.executionRef) === reportedKey) : undefined;
  // A guest works in the owner's Project without being offered it: the conversation itself names it.
  const identity = telemetry.project?.identity;
  const shown = current ?? identity;
  // The picker names the project a conversation runs in, and nothing else. A host target with no project
  // behind it has no name to show, and it is a target that WAS chosen: the label says there is no
  // project, rather than claiming nothing is selected.
  const computer = current?.computer;
  const label = current ? projectName(current)
    : identity?.name ?? (target?.kind === 'local' ? s.computer : t.brainChat.projectPickerHost);
  // A computer's own state and what this conversation is working in on it. A host or managed project has
  // neither, so its trigger keeps the plain kind.
  const localCwd = computer ? telemetry.project?.cwd : undefined;
  const ready = !readOnly && !childFocus && Boolean(activeSessionId) && !projects.isLoading && !projects.isError;

  // The move itself (its no-op, its hold, its single failure card) belongs to the chat controller, so every
  // placement of this picker shares it; this only presents it and follows the environment operation it starts.
  const move = async (next: ProjectExecutionRef) => {
    const session = activeSessionId;
    if (!session) return;
    const response = await selectProjectExecution(next, session);
    // The switch has finished; the shell follows the environment start across conversations and routes.
    if (response?.operationId && sessionRef.current === session) dock.open(response.operationId);
  };
  // Host mode asks nothing. An execution target is a working decision, and the daemon is the authority
  // on whether this account may enter the project — a dialog in front of it only stood between people
  // and the projects they already work in.
  const select = (next: ProjectExecutionRef) => {
    setOpen(false);
    void move(next); // A failure is reported by `move`'s toast; the retry action stays beside the picker.
  };
  return <div data-testid="chat-project-picker" className="relative shrink-0">
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" disabled={moving || moveStatus === 'unconfirmed' || !ready || guest} aria-busy={moving || undefined} title={guest ? t.chat.ownerOnlyControl : localCwd || s.executionKind}
          className={`justify-start gap-1.5 text-muted-foreground hover:text-muted-foreground ${variant === 'compact' ? 'max-w-45 px-2 text-small' : 'max-w-60 px-2.5 text-conversation'}`}>
          {shown ? <ProjectIcon project={shown} size={14} /> : null}
          <span className="min-w-0 truncate">{label}{computer ? (
            <span className="ml-1 text-small text-muted-foreground">
              · <ConnectionStatus connectionState={telemetry.project?.connectionState ?? null} className="inline text-small" />
              {localCwd ? ` · ${localCwd}` : null}
            </span>
          ) : null}</span><ChevronDown size={12} aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      {/* The account-scoped offer includes registered projects and paired local computers.
          Every choice keeps its explicit execution identity through the same authorized endpoint. */}
      <ProjectMenuContent label={s.executionKind} align="end" projects={items}
        selected={target ? executionRefKey(target) : ''} onChoose={(project) => select(project.executionRef)} />
    </DropdownMenu>
    {/* The failure is reported once, by the toast; this is only the way to try again. */}
    {moveStatus === 'error' && retryTarget ? <Button type="button" variant="ghost" disabled={moving} onClick={() => select(retryTarget)}>{t.common.retry}</Button> : null}
  </div>;
}
